<script lang="ts">
  import Chart from '../charts/Chart.svelte';
  import Readout from '../charts/Readout.svelte';
  import { api } from '../../api/client';
  import { climb, length } from '../../utils/activities';
  import { dateTicks, daysBetween, polyline, segments } from '../../utils/chart';
  import { calendar, count, hours, measureAxis, weekRange, type Weeks } from '../../utils/insights';
  import { MEASURE_LABELS, measures, summary, weekStart, type Measure, type Weekly } from '../../utils/progress';
  import { SPORTS, sportOf } from '../../utils/sports';
  import { month, monthDay, t } from '../../i18n/index.ts';

  // WeekSection is This week (spec 006 FR-060, 006 FR-061): sport chips with
  // the member's top sports first, the chosen week's totals, and the one
  // chart of the 13 weeks ending with this one against the 13 before
  // (spec 006 FR-056, 006 FR-057), in the shared chart language (spec 010
  // 010 FR-033). Tapping a total charts it; hovering a week reads it, and
  // tapping one shows its totals, with a button opening it in the training
  // log through onweek.
  let { onweek }: { onweek: (day: string) => void } = $props();

  // The theme's blue and peach, checked for colorblind separation in both
  // themes (spec 010); the prior period is also dashed.
  const THIS_COLOR = 'var(--theme-blue)';
  const PRIOR_COLOR = 'var(--theme-peach)';
  const ws = weekStart(navigator.language);

  let top = $state<string[]>([]);
  let sport = $state('run');
  let measure = $state<Measure>('distance');
  let w = $state<Weeks | null>(null);
  let p = $state<Weekly | null>(null);
  let at = $state<number | null>(null);
  let hover = $state<number | null>(null);
  let error = $state('');

  api.get<string[]>('/me/sports/top').then((got) => {
    top = got;
    if (got[0]) pick(got[0]);
  }).catch(() => {});

  $effect(() => {
    const s = sport;
    at = null;
    api.get<Weeks>(`/me/weekly?sport=${s}&${calendar()}`)
      .then((got) => { w = got; error = ''; })
      .catch((e) => (error = e.message));
  });

  $effect(() => {
    const s = sport, m = measure;
    api.get<Weekly>(`/me/progress?sport=${s}&measure=${m}&${calendar()}`)
      .then((got) => (p = got))
      .catch((e) => (error = e.message));
  });

  // pick changes sport; a sport without a map has no distance or elevation,
  // so it charts time.
  function pick(id: string) {
    sport = id;
    if (!measures(sportOf(id).gps).includes(measure)) measure = 'time';
  }

  // chips are the top sports, then every other sport in catalog order.
  let chips = $derived([...top, ...SPORTS.map((s) => s.id).filter((id) => !top.includes(id))]);
  let gps = $derived(sportOf(sport).gps);
  let cur = $derived(w && w.sport === sport ? w : null);
  let cp = $derived(cur && p && p.sport === sport && p.measure === measure && p.weeks.length === cur.weeks.length ? p : null);
  let last = $derived(cur ? cur.weeks.length - 1 : 0);
  // picked is the selected week, null for this one.
  let picked = $derived(at == null || at === last ? null : at);
  let week = $derived(picked ?? last);
  let shown = $derived(hover ?? at);

  let axis = $derived(measureAxis(measure));
  let scale = $derived(axis.scale(cp ? Math.max(...cp.this, ...cp.prior, 0) * axis.factor : 1));
  let span = $derived(cur ? Math.max(1, daysBetween(cur.weeks[0], cur.weeks[last])) : 1);
  let positions = $derived(cur ? cur.weeks.map((d) => daysBetween(cur!.weeks[0], d) / span) : []);
  let xTicks = $derived.by(() => {
    if (!cur) return [];
    const ticks = dateTicks(cur.weeks[0], cur.weeks[last], 6, ws);
    return ticks.days.map((d) => {
      const on = new Date(`${d}T12:00:00`);
      return { at: daysBetween(cur!.weeks[0], d) / span, text: ticks.unit === 'month' ? month(on, true) : monthDay(on) };
    });
  });
  const line = (vs: number[]) => polyline(segments(vs.map((v, i) => ({ at: positions[i], v: v * axis.factor })))[0] ?? [], scale.lo, scale.hi);

  // weekValue is the chosen week's total of a measure, from This week's totals.
  function weekValue(m: Measure, i: number): string {
    if (!cur) return '';
    switch (m) {
      case 'distance': return length(cur.distance_m[i]);
      case 'time': return hours(cur.moving_s[i]);
      case 'elevation': return climb(cur.elevation_m[i]);
      default: return count(cur.count[i]);
    }
  }

  // value writes a week's or a period's total of the charted measure.
  function value(v: number): string {
    switch (measure) {
      case 'distance': return length(v);
      case 'time': return hours(v);
      case 'elevation': return climb(v);
      default: return count(v);
    }
  }

  const weekName = (i: number) => (i === last ? t('activitiesWeekThis') : t('activitiesWeekOf', { range: weekRange(cur!.weeks[i]) }));
</script>

<section class="card p-4 space-y-3" aria-labelledby="week-heading">
  <div class="flex gap-2 overflow-x-auto pb-1" role="group" aria-label={t('activitiesSport')}>
    {#each chips as id (id)}
      <button class="btn btn-sm shrink-0 {sport === id ? 'btn-primary' : 'btn-secondary'}" aria-pressed={sport === id} onclick={() => pick(id)}>
        <i aria-hidden="true" class="las la-{sportOf(id).icon} mr-1"></i>{sportOf(id).name}
      </button>
    {/each}
  </div>
  {#if error}
    <p class="text-sm" style="color: var(--theme-error);">{error}</p>
  {/if}
  {#if cur}
    <h2 id="week-heading" class="font-semibold">{weekName(week)}</h2>
    <div class="flex flex-wrap gap-1" role="group" aria-label={t('activitiesWeekChart')}>
      {#each measures(gps) as m (m)}
        <button class="text-left rounded-lg px-2.5 py-1.5 hover:bg-surface0" style={measure === m ? 'background: var(--theme-surface1);' : ''}
          aria-pressed={measure === m} onclick={() => (measure = m)}>
          <span class="block text-xs text-text-muted">{m === 'count' && !gps ? t('activitiesWeekSessions') : MEASURE_LABELS[m]}</span>
          <span class="block text-2xl">{weekValue(m, week)}</span>
        </button>
      {/each}
    </div>
    {#if cp}
      {@const i = shown ?? last}
      <Readout title={weekName(i)}
        items={[
          { label: t('activitiesWeekLast3Months'), value: value(cp.this[i]), color: THIS_COLOR },
          { label: t('activitiesWeekSameBefore'), value: value(cp.prior[i]), color: PRIOR_COLOR, mark: 'dash' },
        ]}
        hint={at == null ? t('activitiesWeekHint') : undefined}>
        {#snippet action()}
          {#if at != null}
            <button class="btn btn-secondary btn-sm" onclick={() => onweek(cur!.weeks[at!])}>{t('activitiesOpenInLog')}</button>
          {/if}
        {/snippet}
      </Readout>
      <Chart lo={scale.lo} hi={scale.hi} unit={axis.unit} yTicks={scale.ticks.map((tick) => ({ v: tick, text: axis.tick(tick, scale.step) }))}
        {xTicks} height="h-40" {positions} bind:at bind:hover
        label={t('activitiesWeekChartLabel', { measure })}
        valueText={shown != null ? t('activitiesWeekValueText', { week: weekName(shown), value: value(cp.this[shown]), prior: value(cp.prior[shown]) }) : t('activitiesWeekNonePicked')}
        dots={shown != null ? [{ v: cp.prior[shown] * axis.factor, color: PRIOR_COLOR }, { v: cp.this[shown] * axis.factor, color: THIS_COLOR }] : []}>
        {#snippet plot()}
          <polyline points={line(cp.prior)} fill="none" stroke={PRIOR_COLOR} stroke-width="2" stroke-dasharray="6 4" stroke-linecap="round" vector-effect="non-scaling-stroke" />
          <polyline points={line(cp.this)} fill="none" stroke={THIS_COLOR} stroke-width="2" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
        {/snippet}
      </Chart>
      <p class="font-medium">{summary(cp)}</p>
      <div class="flex flex-wrap gap-x-8 gap-y-2">
        <div>
          <p class="text-xs text-text-muted flex items-center gap-2"><svg width="20" height="8" aria-hidden="true"><line x1="0" y1="4" x2="20" y2="4" stroke={THIS_COLOR} stroke-width="2" /></svg>{t('activitiesWeekLast3Months')}</p>
          <p class="text-xl font-semibold">{value(cp.this_total)}</p>
        </div>
        <div>
          <p class="text-xs text-text-muted flex items-center gap-2"><svg width="20" height="8" aria-hidden="true"><line x1="0" y1="4" x2="20" y2="4" stroke={PRIOR_COLOR} stroke-width="2" stroke-dasharray="4 3" /></svg>{t('activitiesWeekPrior3Months')}</p>
          <p class="text-xl font-semibold">{value(cp.prior_total)}</p>
        </div>
      </div>
      <table class="sr-only">
        <caption>{t('activitiesWeekCaption', { measure, sport: sportOf(sport).name })}</caption>
        <thead><tr><th>{t('activitiesWeekColWeek')}</th><th>{t('activitiesWeekLast3Months')}</th><th>{t('activitiesWeekSameBefore')}</th></tr></thead>
        <tbody>
          {#each cur.weeks as day, i (day)}
            <tr><td>{weekRange(day)}</td><td>{value(cp.this[i])}</td><td>{value(cp.prior[i])}</td></tr>
          {/each}
        </tbody>
      </table>
    {/if}
  {/if}
</section>
